// RESPONSIVENESS — how well ASP looks after the people who email it (Karan's
// "Track mail upgrades" design, turn 4; the ASP Email Tone & Audit Guide §9.6).
// Pure: no DOM, no network, no Firestore. The server (functions/responsiveness.js,
// via the functions/mail-shared copy made by scripts/sync-calc-shared.mjs) and
// the browser (views/responsiveness.js, the Mail reader) decide with this code,
// so the monitor, the drawer and the stats can never disagree.
//
// A QUERY is one incoming external email that needs an ASP reply, tracked per
// thread: when it arrived, when it is due on the working-time clock, when ASP
// first replied, whether that was a holding reply, the promises ASP made and
// whether they were kept, and how often the other side chased.

export const DEFAULT_HOURS = {
  tz: 'Europe/London', start: '09:00', end: '17:30',
  lateAfter: '16:30', lateDue: '12:00', days: [1, 2, 3, 4, 5],   // Mon–Fri
  riskMinutes: 150,                                                 // "At risk" inside the last 2½ working hours (the design shows 2h 10m left as at risk)
};
export const TARGETS = { sameDay: 0.9, chase: 0.1, promiseKept: 0.9, recommendation: 0.9 };
const EVENTS_CAP = 60;

// ── Time in the office's zone ───────────────────────────────────────────────
const hm = (s) => { const [h, m] = String(s).split(':').map(Number); return h * 60 + (m || 0); };
const fmtCache = new Map();
function fmt(tz) {
  if (!fmtCache.has(tz)) {
    fmtCache.set(tz, new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', weekday: 'short',
    }));
  }
  return fmtCache.get(tz);
}
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
/** A moment → its wall-clock parts in `tz`. */
export function zoned(date, tz = DEFAULT_HOURS.tz) {
  const p = Object.fromEntries(fmt(tz).formatToParts(new Date(date)).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, dow: DOW[p.weekday] };
}
/** Wall-clock parts in `tz` → the moment. Two passes settle a DST edge. */
export function fromZoned(y, m, d, mins, tz = DEFAULT_HOURS.tz) {
  const want = Date.UTC(y, m - 1, d, Math.floor(mins / 60), mins % 60);
  let t = want;
  for (let i = 0; i < 2; i++) {
    const z = zoned(t, tz);
    const got = Date.UTC(z.y, z.m - 1, z.d, z.h, z.mi);
    t += want - got;
  }
  return new Date(t);
}
const addDays = (y, m, d, n) => { const t = new Date(Date.UTC(y, m - 1, d + n)); return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), dow: t.getUTCDay() }; };
const isWorking = (dow, H) => H.days.includes(dow);
function nextWorkingDay(y, m, d, H) {
  for (let i = 1; i < 14; i++) { const n = addDays(y, m, d, i); if (isWorking(n.dow, H)) return n; }
  return addDays(y, m, d, 1);
}

/**
 * When a reply is due. Same working day; after `lateAfter` (16:30) it is due
 * at `lateDue` (12:00) the next working day; a weekend or before-hours arrival
 * is due by the end of the next working day it falls into.
 */
export function dueAt(arrived, H = DEFAULT_HOURS) {
  const z = zoned(arrived, H.tz);
  const mins = z.h * 60 + z.mi;
  if (isWorking(z.dow, H) && mins < hm(H.lateAfter)) return fromZoned(z.y, z.m, z.d, hm(H.end), H.tz);
  const n = nextWorkingDay(z.y, z.m, z.d, H);
  if (isWorking(z.dow, H)) return fromZoned(n.y, n.m, n.d, hm(H.lateDue), H.tz);   // late arrival
  return fromZoned(n.y, n.m, n.d, hm(H.end), H.tz);                               // weekend
}

/** Working minutes from a to b (negative when b is before a). */
export function workingMinutes(a, b, H = DEFAULT_HOURS) {
  const ta = +new Date(a); const tb = +new Date(b);
  if (tb < ta) return -workingMinutes(b, a, H);
  let total = 0;
  let z = zoned(ta, H.tz);
  for (let i = 0; i < 400; i++) {
    if (isWorking(z.dow, H)) {
      const open = +fromZoned(z.y, z.m, z.d, hm(H.start), H.tz);
      const close = +fromZoned(z.y, z.m, z.d, hm(H.end), H.tz);
      const s = Math.max(open, ta); const e = Math.min(close, tb);
      if (e > s) total += (e - s) / 60000;
      if (close >= tb) break;
    }
    const n = addDays(z.y, z.m, z.d, 1);
    if (+fromZoned(n.y, n.m, n.d, 0, H.tz) > tb) break;
    z = { ...n, h: 0, mi: 0 };
  }
  return Math.round(total);
}

export const fmtMinutes = (m) => { const a = Math.abs(Math.round(m)); const h = Math.floor(a / 60); const mm = a % 60; return h ? `${h}h ${String(mm).padStart(2, '0')}m` : `${mm}m`; };

// ── Reading an incoming email ───────────────────────────────────────────────
const CHASE = /\b(chasing|any (update|news|progress)|following up|follow(ing)?[- ]up on|gentle reminder|just a reminder|still waiting|still awaiting|urgent(ly)?|have you had (a )?chance|did you (get|receive|see)|i haven'?t heard|not heard back)\b/i;
const REQUEST = /\b(can|could|would|will) you\b|\bplease (send|confirm|advise|let|provide|review|check|could|can|update|share|forward|arrange|call|ring)\b|\bkindly\b|\blet (me|us) know\b|\bwhen (will|can|do|are)\b|\bdo you (have|know|need)\b|\bare you (able|available|happy)\b|\bwhat (is|are|was|would)\b|\bhow (do|can|much|long|soon)\b|\bis it possible\b|\bwe (need|require) (your|a|the)\b|\bawait(ing)? your\b/i;
const ACK = /^(?:hi|hello|dear)?[^\n]{0,40}?\b(thanks?|thank you|many thanks|cheers|noted|great|perfect|brilliant|received|will do|sounds good|that'?s fine|ok(ay)?)\b[\s\S]{0,60}$/i;
const NOT_PEOPLE = /(no-?reply|notifications?|mailer-daemon|newsletter|marketing|bounce|donotreply)@/i;

export const isChaseWording = (text) => CHASE.test(String(text || ''));

/**
 * The code half of "is this a query that needs an ASP reply?" — true / false
 * when the wording is plain, null when the model should decide.
 */
export function classifyCode({ from = '', subject = '', snippet = '' } = {}) {
  const text = `${subject}\n${snippet}`.trim();
  if (NOT_PEOPLE.test(from)) return { isQuery: false, reason: 'automated sender' };
  if (/\bunsubscribe\b|view (this|it) in your browser|manage (your )?preferences/i.test(snippet)) return { isQuery: false, reason: 'newsletter or notification' };
  if (isChaseWording(text)) return { isQuery: true, reason: 'chasing wording' };
  if (/\?/.test(snippet) || /\?\s*$/.test(subject)) return { isQuery: true, reason: 'asks a question' };
  if (REQUEST.test(text)) return { isQuery: true, reason: 'makes a request' };
  const words = (snippet.match(/\S+/g) || []).length;
  if (words <= 25 && ACK.test(snippet.trim())) return { isQuery: false, reason: 'acknowledgement' };
  return { isQuery: null, reason: 'unclear from the wording' };
}

// ── Reading an outgoing ASP reply ───────────────────────────────────────────
const HOLDING = /\b(looking into (this|it)|(we|i)(?:'ll| will) (come back|get back|revert|respond|reply|send (it|this|them|our|you)|have (this|it|that))|reviewing (this|it)|bear with (us|me)|(thank you|thanks),? received|received,? (thank you|thanks)|will (come back|get back|revert))\b/i;
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * A time ASP committed to, as words and a moment: "by 3pm", "by 15:00", "by
 * end of day", "by tomorrow morning", "by Thursday", "later today". Null if
 * none. Unspecified times land at the end of the working day.
 */
export function extractPromise(text, sentAt, H = DEFAULT_HOURS) {
  const s = String(text || '');
  const z = zoned(sentAt, H.tz);
  const today = { y: z.y, m: z.m, d: z.d };
  const at = (day, mins) => fromZoned(day.y, day.m, day.d, mins, H.tz);
  const tomorrow = () => nextWorkingDay(z.y, z.m, z.d, H);
  let m = s.match(/\bby\s+(\d{1,2})(?::(\d{2}))?\s?(am|pm)\b(\s+(today|tomorrow))?/i);
  if (m) {
    let h = +m[1] % 12; if (/pm/i.test(m[3])) h += 12;
    const day = /tomorrow/i.test(m[5] || '') ? tomorrow() : today;
    return { text: m[0], dueAt: at(day, h * 60 + (+m[2] || 0)) };
  }
  m = s.match(/\bby\s+([01]?\d|2[0-3]):([0-5]\d)\b(\s+(today|tomorrow))?/i);
  if (m) return { text: m[0], dueAt: at(/tomorrow/i.test(m[4] || '') ? tomorrow() : today, +m[1] * 60 + +m[2]) };
  m = s.match(/\bby\s+(the\s+)?(end of (the )?(day|today)|close of (play|business)|cob|eod)\b/i);
  if (m) return { text: m[0], dueAt: at(today, hm(H.end)) };
  m = s.match(/\b(by|before)\s+(tomorrow|the end of tomorrow)(\s+(morning|lunchtime|afternoon|evening))?/i);
  if (m) return { text: m[0], dueAt: at(tomorrow(), /morning|lunchtime/i.test(m[4] || '') ? hm(H.lateDue) : hm(H.end)) };
  m = s.match(new RegExp(`\\b(by|before|on)\\s+(${WEEKDAYS.join('|')})(\\s+(morning|lunchtime|afternoon))?`, 'i'));
  if (m) {
    const want = WEEKDAYS.indexOf(m[2].toLowerCase());
    let day = { ...today, dow: z.dow };
    for (let i = 0; i < 8 && day.dow !== want; i++) day = addDays(day.y, day.m, day.d, 1);
    return { text: m[0], dueAt: at(day, /morning|lunchtime/i.test(m[4] || '') ? hm(H.lateDue) : hm(H.end)) };
  }
  m = s.match(/\b(later today|this afternoon|by today|today)\b/i);
  if (m && /\b(will|we'll|i'll|by)\b/i.test(s)) return { text: m[0], dueAt: at(today, hm(H.end)) };
  return null;
}

/** A holding reply: a promise or holding wording, short, and nothing attached. */
export function isHoldingReply(text, attachments = 0) {
  if (attachments > 0) return false;
  const words = (String(text || '').match(/\S+/g) || []).length;
  return words <= 90 && HOLDING.test(String(text || ''));
}

// ── The state machine ───────────────────────────────────────────────────────
const iso = (d) => new Date(d).toISOString();
const ev = (q, e) => { q.events = [...(q.events || []), e].slice(-EVENTS_CAP); };
const excluded = (q) => q && q.classifier && q.classifier.corrected && q.classifier.corrected.isQuery === false;

/**
 * An incoming external message on a thread. Creates the query (if it is one),
 * counts a chase when ASP has not answered yet, or reopens a replied thread.
 * m: { id, at, from, subject, snippet, isQuery, classifier, owner, projectNo }
 */
export function applyIncoming(q, m, H = DEFAULT_HOURS) {
  if (!q) {
    if (!m.isQuery) return null;
    const out = {
      threadId: m.threadId, from: m.from || '', clientDomain: domainOf(m.from), subject: m.subject || '',
      owner: m.owner || '', projectNo: m.projectNo || null,
      arrivedAt: iso(m.at), dueAt: iso(dueAt(m.at, H)), state: 'open',
      firstReplyAt: null, holdingAt: null, repliedAt: null, chases: 0, extMessages: 1, aspMessages: 0,
      promises: [], sentiment: m.sentiment || null,
      classifier: m.classifier || { isQuery: true, reason: 'query', source: 'code' }, events: [],
    };
    ev(out, { at: iso(m.at), kind: 'arrived', detail: `${(out.classifier.reason || 'query')} (${out.classifier.source || 'code'})`, messageId: m.id || null, by: m.from || null });
    return out;
  }
  const out = { ...q, extMessages: (q.extMessages || 0) + 1 };
  if (m.sentiment) out.sentiment = m.sentiment;
  if (q.state === 'open' || q.state === 'holding') {
    out.chases = (q.chases || 0) + 1;
    ev(out, { at: iso(m.at), kind: 'chase', detail: isChaseWording(`${m.subject} ${m.snippet}`) ? 'chased before our reply' : 'wrote again before our reply', messageId: m.id || null, by: m.from || null });
  } else if (m.isQuery) {
    out.state = 'open'; out.reopenedAt = iso(m.at); out.dueAt = iso(dueAt(m.at, H));
    if (isChaseWording(`${m.subject} ${m.snippet}`)) out.chases = (q.chases || 0) + 1;
    ev(out, { at: iso(m.at), kind: 'reopened', detail: 'a new question on the thread', messageId: m.id || null, by: m.from || null });
  } else {
    ev(out, { at: iso(m.at), kind: 'message', detail: 'a reply that needs nothing from us', messageId: m.id || null, by: m.from || null });
  }
  return out;
}

/**
 * An ASP message on a monitored thread. First reply, holding or full; any
 * promise it makes; and any earlier promise it keeps.
 * m: { id, at, text, attachments, by }
 */
export function applyOutgoing(q, m, H = DEFAULT_HOURS) {
  if (!q) return null;
  const out = { ...q, aspMessages: (q.aspMessages || 0) + 1, promises: [...(q.promises || [])] };
  const holding = isHoldingReply(m.text, m.attachments || 0);
  const promise = extractPromise(m.text, m.at, H);
  if (!out.firstReplyAt) out.firstReplyAt = iso(m.at);
  if (!holding) {
    // A full reply (or one carrying the work) keeps every promise still open and due after it.
    out.promises = out.promises.map((p) => (p.kept == null && +new Date(p.dueAt) >= +new Date(m.at)
      ? { ...p, kept: true, keptBy: m.id || null, keptAt: iso(m.at) } : p));
  }
  if (promise) out.promises.push({ text: promise.text, dueAt: iso(promise.dueAt), kept: null, keptBy: null, messageId: m.id || null });
  if (holding) {
    out.state = 'holding'; if (!out.holdingAt) out.holdingAt = iso(m.at);
    ev(out, { at: iso(m.at), kind: 'holding', detail: promise ? `holding reply · promised ${promise.text}` : 'holding reply · no time given', messageId: m.id || null, by: m.by || null });
  } else {
    out.state = 'replied'; out.repliedAt = iso(m.at);
    ev(out, { at: iso(m.at), kind: 'replied', detail: promise ? `replied · promised ${promise.text}` : 'replied', messageId: m.id || null, by: m.by || null });
  }
  return out;
}

/** Due promises with nothing sent become missed; called by the 15-minute sweep. */
export function sweep(q, now = Date.now()) {
  let changed = false;
  const promises = (q.promises || []).map((p) => {
    if (p.kept == null && +new Date(p.dueAt) < +new Date(now)) { changed = true; return { ...p, kept: false }; }
    return p;
  });
  if (!changed) return null;
  const out = { ...q, promises };
  promises.filter((p, i) => p.kept === false && (q.promises[i] || {}).kept == null)
    .forEach((p) => ev(out, { at: iso(p.dueAt), kind: 'missed', detail: `promise missed: ${p.text}`, messageId: p.messageId || null, by: null }));
  return out;
}

/** Where the query stands on the clock now: breached / risk / open, with minutes. */
export function clockOf(q, now = Date.now(), H = DEFAULT_HOURS) {
  if (!q || q.state === 'replied' || q.state === 'closed') return { state: 'done', minutes: 0, against: null };
  const open = (q.promises || []).filter((p) => p.kept == null).map((p) => new Date(p.dueAt)).sort((a, b) => a - b)[0];
  const against = q.state === 'holding' && open ? open : new Date(q.dueAt);
  // Past due on the wall clock is breached even out of hours (the working-time
  // overrun is then 0); the figure shown is working time over, or wall time if none.
  if (+new Date(now) > +against) {
    const over = workingMinutes(against, now, H) || Math.round((+new Date(now) - +against) / 60000);
    return { state: 'breached', minutes: -over, against: against.toISOString(), kind: q.state === 'holding' && open ? 'promise' : 'reply' };
  }
  const left = workingMinutes(now, against, H);
  const state = left <= H.riskMinutes ? 'risk' : 'open';
  return { state, minutes: left, against: against.toISOString(), kind: q.state === 'holding' && open ? 'promise' : 'reply' };
}

/** One line for the Mail reader: "Open · holding sent Tue · promise due Thu 5:30pm". */
export function statusLine(q, now = Date.now(), H = DEFAULT_HOURS) {
  if (!q || excluded(q)) return '';
  const day = (d) => { const z = zoned(d, H.tz); const nz = zoned(now, H.tz); return z.y === nz.y && z.m === nz.m && z.d === nz.d ? 'today' : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][z.dow]; };
  const time = (d) => { const z = zoned(d, H.tz); const h = z.h % 12 || 12; return `${h}${z.mi ? `:${String(z.mi).padStart(2, '0')}` : ''}${z.h < 12 ? 'am' : 'pm'}`; };
  if (q.state === 'replied') return `Replied ${day(q.repliedAt)}`;
  if (q.state === 'closed') return 'Closed';
  const bits = [q.state === 'holding' ? 'Open' : 'Open'];
  if (q.state === 'holding' && q.holdingAt) bits.push(`holding sent ${day(q.holdingAt)}`);
  const open = (q.promises || []).filter((p) => p.kept == null).sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt))[0];
  if (open) bits.push(`promise due ${day(open.dueAt)} ${time(open.dueAt)}`);
  else bits.push(`reply due ${day(q.dueAt)} ${time(q.dueAt)}`);
  if (q.chases) bits.push(`chased ${q.chases === 1 ? 'once' : `${q.chases} times`}`);
  return bits.join(' · ');
}

// ── The figures ─────────────────────────────────────────────────────────────
export function domainOf(addr) { return String(addr || '').toLowerCase().replace(/^.*</, '').replace(/>.*$/, '').split('@')[1] || ''; }
/** The Monday (office zone) a moment falls in, as YYYY-MM-DD. */
export function weekKey(date, H = DEFAULT_HOURS) {
  const z = zoned(date, H.tz);
  const back = (z.dow + 6) % 7;
  const mo = addDays(z.y, z.m, z.d, -back);
  return `${mo.y}-${String(mo.m).padStart(2, '0')}-${String(mo.d).padStart(2, '0')}`;
}
const median = (xs) => { if (!xs.length) return null; const s = [...xs].sort((a, b) => a - b); const i = Math.floor(s.length / 2); return s.length % 2 ? s[i] : Math.round((s[i - 1] + s[i]) / 2); };
const rate = (num, den) => (den ? num / den : null);

/**
 * The five headline figures (§9.6) over a set of queries. Same-day counts a
 * query once its due time has passed or it has been answered; promises count
 * once decided. Recommendation rate needs the email audit's decision-email
 * check, which is not built yet — it stays null rather than invented.
 */
export function figures(queries, now = Date.now(), H = DEFAULT_HOURS) {
  const qs = queries.filter((q) => !excluded(q));
  const judged = qs.filter((q) => q.firstReplyAt || +new Date(q.dueAt) < +new Date(now));
  const onTime = judged.filter((q) => q.firstReplyAt && +new Date(q.firstReplyAt) <= +new Date(q.dueAt));
  const promises = qs.flatMap((q) => q.promises || []).filter((p) => p.kept != null);
  const closed = qs.filter((q) => q.state === 'replied' || q.state === 'closed');
  const firsts = qs.filter((q) => q.firstReplyAt).map((q) => workingMinutes(q.arrivedAt, q.firstReplyAt, H));
  return {
    n: qs.length,
    open: qs.filter((q) => q.state === 'open' || q.state === 'holding').length,
    sameDay: rate(onTime.length, judged.length), sameDayN: judged.length,
    chase: rate(qs.filter((q) => (q.chases || 0) > 0).length, qs.length),
    promiseKept: rate(promises.filter((p) => p.kept === true).length, promises.length), promisesN: promises.length,
    messagesToClose: closed.length ? Math.round((closed.reduce((n, q) => n + (q.extMessages || 0) + (q.aspMessages || 0), 0) / closed.length) * 10) / 10 : null,
    recommendation: null,
    medianFirstReply: median(firsts),
  };
}

/** Weekly figures for the last `weeks` Mondays, oldest first — the sparklines. */
export function weekly(queries, now = Date.now(), weeks = 12, H = DEFAULT_HOURS) {
  const keys = [];
  for (let i = weeks - 1; i >= 0; i--) keys.push(weekKey(new Date(+new Date(now) - i * 7 * 86400000), H));
  const by = new Map(keys.map((k) => [k, []]));
  queries.forEach((q) => { const k = weekKey(q.arrivedAt, H); if (by.has(k)) by.get(k).push(q); });
  return keys.map((k) => ({ week: k, ...figures(by.get(k), now, H) }));
}

/** By person, workload first — never a leaderboard. */
export function byPerson(queries, now = Date.now(), H = DEFAULT_HOURS) {
  const groups = new Map();
  queries.filter((q) => !excluded(q)).forEach((q) => { const k = q.owner || 'unassigned'; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(q); });
  return [...groups.entries()].map(([owner, qs]) => ({ owner, ...figures(qs, now, H) }))
    .sort((a, b) => b.open - a.open || a.owner.localeCompare(b.owner));
}

/** Clients to act on before they call: 2+ chases or a missed promise in 30 days. */
export function needingAttention(queries, now = Date.now()) {
  const since = +new Date(now) - 30 * 86400000;
  const groups = new Map();
  queries.filter((q) => !excluded(q) && +new Date(q.arrivedAt) >= since && q.clientDomain).forEach((q) => {
    const g = groups.get(q.clientDomain) || { domain: q.clientDomain, chases: 0, missed: 0, projects: new Set(), last: null };
    g.chases += q.chases || 0;
    g.missed += (q.promises || []).filter((p) => p.kept === false).length;
    if (q.projectNo) g.projects.add(String(q.projectNo));
    const lastMiss = (q.promises || []).filter((p) => p.kept === false).map((p) => p.dueAt).sort().pop();
    if (lastMiss && (!g.last || lastMiss > g.last)) g.last = lastMiss;
    groups.set(q.clientDomain, g);
  });
  return [...groups.values()].filter((g) => g.chases >= 2 || g.missed > 0)
    .map((g) => ({ domain: g.domain, chases: g.chases, missed: g.missed, projects: [...g.projects], lastMissed: g.last,
      reason: g.missed ? `Missed promise${g.missed > 1 ? `s (${g.missed})` : ''}` : `${g.chases} chases in the last 30 days` }))
    .sort((a, b) => b.missed - a.missed || b.chases - a.chases);
}

/** Promises falling due today (office zone), still undecided or decided today. */
export function promisesDueToday(queries, now = Date.now(), H = DEFAULT_HOURS) {
  const nz = zoned(now, H.tz);
  return queries.filter((q) => !excluded(q)).flatMap((q) => (q.promises || []).map((p, i) => ({ q, p, i })))
    .filter(({ p }) => { const z = zoned(p.dueAt, H.tz); return z.y === nz.y && z.m === nz.m && z.d === nz.d; })
    .sort((a, b) => new Date(a.p.dueAt) - new Date(b.p.dueAt));
}

// ── The "add a time" prompt in Compose ──────────────────────────────────────
// The email audit's LEAD-04 (holding reply without a firm time) and COM-03
// ("ASAP", "shortly") — copied VERBATIM from functions/email-audit/asp_rules.json
// (test/responsiveness.test.mjs fails if they drift) — plus the contracted
// forms ("we'll come back to you") the guide's starter regex misses.
export const AUDIT_PATTERNS = {
  'LEAD-04': '\\b(looking into (this|it)|will (come back|get back|revert)|reviewing (this|it))\\b(?![^.]{0,60}\\b(by|at|before)\\b)',
  'COM-03': '\\b(asap|a\\.s\\.a\\.p|as soon as possible|shortly|in due course)\\b',
};
const CONTRACTED = "\\b(?:we|i)'ll (?:come back|get back|revert|respond|reply)\\b(?![^.]{0,60}\\b(?:by|at|before)\\b)";

/** The holding phrase in `text` that gives no time, or null. */
export function holdingWithoutTime(text) {
  const s = String(text || '');
  for (const p of [AUDIT_PATTERNS['LEAD-04'], CONTRACTED, AUDIT_PATTERNS['COM-03']]) {
    const m = s.match(new RegExp(p, 'i'));
    if (m && !extractPromiseIn(s, m.index)) return { phrase: m[0], at: m.index };
  }
  return null;
}
function extractPromiseIn(s, at) {
  const sentenceEnd = s.slice(at).search(/[.!?\n]/);
  const sentence = s.slice(at, sentenceEnd < 0 ? undefined : at + sentenceEnd);
  return /\b(by|before|on)\s+(\d|today|tomorrow|monday|tuesday|wednesday|thursday|friday|end of|close of|cob|eod)/i.test(sentence) || /\b(today|this afternoon|tomorrow)\b/i.test(sentence);
}
